import { useState } from 'react';
import { Download, FileSpreadsheet, Share2, TriangleAlert } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { Banner } from '@/components/Banner';
import { BotonEnlace, Button } from '@/components/Button';
import { Card, Emblema, Seccion } from '@/components/Card';
import { aBlobDeExcel, prepararExcel, type ExcelListo } from '@/db/excel';
import { mensajeDeError } from '@/db/tx';
import { leerSettings } from '@/db/schema';
import { formatearNumero } from '@/domain/numbers';
import { compartirBlob, descargarBlob, puedeCompartirArchivos } from '@/lib/file-transfer';
import { useLiveQuery } from 'dexie-react-hooks';

interface Archivo extends ExcelListo {
  blob: Blob;
}

/**
 * Exportar a Excel (Req. 28). En dos pasos, como el respaldo: primero se
 * prepara el archivo (ahí se lee todo y se arma el .xlsx) y luego se entrega,
 * porque en iOS compartir solo funciona si se llama en el mismo toque.
 *
 * La pantalla dice claro que esto NO es un respaldo y que no se puede
 * importar (Req. 28.3): es para mirar los datos, no para moverlos.
 */
export function ExcelPage() {
  const ajustes = useLiveQuery(() => leerSettings(), []);
  const [archivo, setArchivo] = useState<Archivo | null>(null);
  const [preparando, setPreparando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<{ tono: 'info' | 'aviso'; titulo: string; texto: string } | null>(null);
  const puedeCompartir = puedeCompartirArchivos();

  async function preparar() {
    if (preparando) return;
    setPreparando(true);
    setError(null);
    setAviso(null);
    try {
      const listo = await prepararExcel();
      setArchivo({ ...listo, blob: await aBlobDeExcel(listo.hojas) });
    } catch (e) {
      setError(mensajeDeError(e));
    } finally {
      setPreparando(false);
    }
  }

  async function compartir(a: Archivo) {
    const compartido = await compartirBlob(a.nombre, a.blob, 'Mis datos de DuoFit');
    setAviso(
      compartido
        ? { tono: 'info', titulo: 'Compartido', texto: `${a.nombre}, desde el menú del iPhone.` }
        : {
            tono: 'aviso',
            titulo: 'No se compartió',
            texto: 'Se cerró el menú o el iPhone no lo permitió. "Guardar en Archivos" es la vía más confiable.',
          },
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        titulo="Tus datos en Excel."
        descripcion="Para mirarlos a tu manera, en Excel o en Google Sheets."
        volver={{ a: '/mas', texto: 'Volver', etiqueta: 'Volver a Más' }}
      />

      {/* Req. 28.3: que no se confunda con un respaldo */}
      <Banner tono="aviso" titulo="El Excel no es un respaldo">
        <p>
          Este archivo no se puede volver a importar a DuoFit: sirve para mirar y analizar, no para
          recuperar tus datos. Lo que protege tu información es el respaldo.
        </p>
        <div className="mt-3">
          <BotonEnlace to="/mas/respaldos" variante="secundario" icono={TriangleAlert}>
            Ir a Respaldos
          </BotonEnlace>
        </div>
      </Banner>

      {archivo === null ? (
        <Card>
          <Emblema icono={FileSpreadsheet} />
          <h2 className="mb-2 mt-3 text-[22px] leading-tight tracking-[-0.6px] text-texto">Una hoja por cada cosa</h2>
          <p className="text-[13px] leading-relaxed text-texto-suave">
            Entrenamientos (una fila por serie), pesajes, medidas, comidas, totales por día, ayunos y
            etapas de tus planes.
          </p>
          <p className="mt-2 text-[13px] leading-relaxed text-texto-suave">
            Los pesos van en {ajustes?.unidadPeso ?? 'kg'} y las medidas en {ajustes?.unidadLongitud ?? 'cm'}, tus
            unidades, con lo que escribiste aparte. Las fechas van como texto (AAAA-MM-DD), para que no se
            desordenen al abrir el archivo.
          </p>
          <div className="mt-5">
            <Button icono={FileSpreadsheet} disabled={preparando} onClick={() => void preparar()}>
              {preparando ? 'Preparando el archivo…' : 'Preparar el archivo'}
            </Button>
          </div>
        </Card>
      ) : (
        <Seccion titulo="Archivo listo" descripcion={archivo.nombre}>
          <div className="space-y-4">
            {archivo.filas === 0 ? (
              <Banner tono="info" titulo="Todavía no hay nada que exportar">
                Las hojas irían con sus encabezados y sin filas. Registra algo y vuelve.
              </Banner>
            ) : (
              <>
                <Card>
                  <dl className="divide-y divide-borde">
                    {archivo.resumen.map((h) => (
                      <div key={h.nombre} className="flex items-baseline justify-between gap-3 py-2">
                        <dt className="text-[13px] text-texto">{h.nombre}</dt>
                        <dd className="text-[13px] tabular-nums text-texto-suave">
                          {h.filas === 0 ? 'sin filas' : `${formatearNumero(h.filas, { decimales: 0 })} ${h.filas === 1 ? 'fila' : 'filas'}`}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </Card>
                <div className="grid gap-2">
                  <Button icono={Download} onClick={() => descargarBlob(archivo.nombre, archivo.blob)}>
                    Guardar en Archivos
                  </Button>
                  {puedeCompartir && (
                    <Button variante="secundario" icono={Share2} onClick={() => void compartir(archivo)}>
                      Compartir
                    </Button>
                  )}
                </div>
              </>
            )}
            <Button variante="fantasma" onClick={() => setArchivo(null)}>
              Volver a prepararlo
            </Button>
            {aviso !== null && (
              <Banner tono={aviso.tono} titulo={aviso.titulo}>
                {aviso.texto}
              </Banner>
            )}
          </div>
        </Seccion>
      )}

      {error !== null && (
        <Banner tono="alerta" titulo="No se pudo preparar">
          {error}
        </Banner>
      )}
    </div>
  );
}
